/**
 * The four setup steps in the rail, and which one to do next - the prototype's _step_caption and
 * _next_step, as plain functions so they can be tested without rendering anything.
 */

import type { Draft } from "./draft";
import type { RunRecord, Study } from "@/lib/queries";

export type StepState = "done" | "next" | "todo";

export type Step = {
  number: 1 | 2 | 3 | 4;
  label: string;
  value: string | null; // what was chosen, shown in green; null means "not set"
};

export function stepsFor(study: Study | undefined, draft: Draft, runs: RunRecord[]): Step[] {
  if (study) {
    const working = (study.variants ?? []).find((v) => v.variant_id === study.working_variant_id);
    const facts = working?.locked_facts ?? study.locked_facts ?? [];
    const results = runs.filter((r) => r.status === "done").length;
    return [
      { number: 1, label: "Audience", value: study.audience_name ?? "General public" },
      { number: 2, label: "Message", value: working ? working.label : null },
      { number: 3, label: "Promises", value: facts.length ? `${facts.length} kept` : "none" },
      { number: 4, label: "Ask", value: results ? `${results} result${results === 1 ? "" : "s"}` : null },
    ];
  }
  return [
    { number: 1, label: "Audience", value: draft.audienceName },
    { number: 2, label: "Message", value: draft.text.trim().length >= 20 ? draft.name.trim() || "Draft" : null },
    { number: 3, label: "Promises", value: draft.factsSaved ? (draft.facts.length ? `${draft.facts.length} kept` : "none") : null },
    { number: 4, label: "Ask", value: null },
  ];
}

/** The first step without a value, or 0 when all four are done. */
export function nextStep(steps: Step[]): number {
  return steps.find((s) => !s.value)?.number ?? 0;
}

export function stateOf(step: Step, next: number): StepState {
  if (step.value) return "done";
  return step.number === next ? "next" : "todo";
}
